// frontend/src/components/Message.js
import React from 'react';
import '../styles/Message.css';
import 'katex/dist/katex.min.css'; // Importa il CSS di KaTeX
import { InlineMath, BlockMath } from 'react-katex';

const Message = ({ message, isTyping }) => {
  const { role, content, created_at } = message;
  
  // Formatta la data e ora del messaggio
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
  };
  
  // Determina l'avatar in base al ruolo
  const getAvatar = (role) => {
    switch(role) {
      case 'user':
        return '/images/user.png';
      case 'assistant':
        return '/images/duck.png';
      case 'system':
        return '/images/system.png';
      default:
        return '/images/default.png';
    }
  };
  
  // Formatta il testo con supporto per LaTeX e markdown
  const renderContent = () => {
    if (!content) return null;
    
    // Cerca per blocchi LaTeX delimitati da $$ ... $$
    const blockRegex = /\$\$(.*?)\$\$/gs;
    // Cerca per inline LaTeX delimitato da $ ... $ ma non $$ ... $$
    const inlineRegex = /(?<!\$)\$(?!\$)(.*?)(?<!\$)\$(?!\$)/g;
    
    // Dividi il testo in parti, mantenendo i delimitatori
    let parts = [];
    let lastIndex = 0;
    
    // Prima gestiamo i blocchi LaTeX (hanno priorità)
    let blockMatch;
    const workingContent = content.slice();
    const tempParts = [];
    
    // Trova tutti i blocchi matematici
    while ((blockMatch = blockRegex.exec(workingContent)) !== null) {
      // Aggiungi il testo prima del blocco
      if (blockMatch.index > lastIndex) {
        tempParts.push({
          type: 'text',
          content: workingContent.substring(lastIndex, blockMatch.index)
        });
      }
      
      // Aggiungi il blocco LaTeX
      tempParts.push({
        type: 'block-math',
        content: blockMatch[1].trim()
      });
      
      lastIndex = blockMatch.index + blockMatch[0].length;
    }
    
    // Aggiungi eventuale testo rimanente
    if (lastIndex < workingContent.length) {
      tempParts.push({
        type: 'text',
        content: workingContent.substring(lastIndex)
      });
    }
    
    // Ora processa tutti i pezzi di testo per trovare inline LaTeX
    tempParts.forEach(part => {
      if (part.type !== 'text') {
        parts.push(part);
        return;
      }
      
      let textContent = part.content;
      lastIndex = 0;
      let inlineMatch;
      
      while ((inlineMatch = inlineRegex.exec(textContent)) !== null) {
        // Aggiungi il testo prima dell'inline
        if (inlineMatch.index > lastIndex) {
          parts.push({
            type: 'text',
            content: textContent.substring(lastIndex, inlineMatch.index)
          });
        }
        
        // Aggiungi l'inline LaTeX
        parts.push({
          type: 'inline-math',
          content: inlineMatch[1].trim()
        });
        
        lastIndex = inlineMatch.index + inlineMatch[0].length;
      }
      
      // Aggiungi eventuale testo rimanente
      if (lastIndex < textContent.length) {
        parts.push({
          type: 'text',
          content: textContent.substring(lastIndex)
        });
      }
    });
    
    // Formatta le parti non LaTeX con markdown semplice
    const formatText = (text) => {
      return text
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/`([^`]+)`/g, '<code>$1</code>')
        .replace(/\n/g, '<br />');
    };
    
    // Renderizza tutte le parti
    return parts.map((part, index) => {
      switch (part.type) {
        case 'block-math':
          return <div key={index} className="latex-block"><BlockMath math={part.content} /></div>;
        case 'inline-math':
          return <InlineMath key={index} math={part.content} />;
        case 'text':
        default:
          return <span key={index} dangerouslySetInnerHTML={{ __html: formatText(part.content) }} />;
      }
    });
  };
  
  return (
    <div className={`message ${role}-message`}>
      <div className="message-avatar">
        <img src={getAvatar(role)} alt={role} />
      </div>
      <div className="message-content">
        {isTyping ? (
          <div className="typing-indicator">
            <span></span>
            <span></span>
            <span></span>
          </div>
        ) : (
          <>
            <div className="message-text">
              {renderContent()}
            </div>
            {created_at && (
              <div className="message-time">
                {formatTime(created_at)}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Message;